import apisauce from 'apisauce'
import type { AxiosInstance } from 'axios'
import Config from 'APP/Config'
import { isAccessTokenValid } from 'APP/Lib/AuthHelpers'
import { Silkroad } from '@dialogue/services'
import {
  AvailabilitiesApiFactory,
  Configuration,
  type CancelAppointmentResponse,
  type CreateChargeResponse,
  type DateAvailabilitiesResponse,
  type DateAvailabilitiesWithPreferredResponse,
  type FlowType,
  type SearchAllProvidersRequest,
  type SearchAvailabilitiesV3Request,
  type StartFlowPayload,
} from '@dialogue/silkroad'
import { serviceHeaders } from 'APP/Lib/ServiceHeaders'
import { logger } from 'APP/Lib/Datadog'

const create = (
  accessToken: string | null,
  baseURL: string | null | undefined = Config.SILKROAD_DOMAIN,
  timeout: number | undefined = undefined
) => {
  if (!baseURL) {
    throw new Error('Unable to connect to Silkroad. No base URL provided.')
  }

  if (!isAccessTokenValid(accessToken)) {
    throw new Error('Unable to connect to Silkroad. Access token is not valid.')
  }

  const dialogueServicesClient = new Silkroad(accessToken, baseURL, timeout, serviceHeaders)

  const api = apisauce.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...serviceHeaders,
    },
    timeout: timeout || 30000,
  })

  // Shares createCharge's apisauce instance and its headers and timeout; apisauce's older axios needs the cast.
  const availabilitiesApi = AvailabilitiesApiFactory(
    new Configuration({ accessToken, basePath: baseURL, baseOptions: { headers: serviceHeaders } }),
    '',
    api.axiosInstance as AxiosInstance
  )

  const getTimeBasedAvailabilitiesV3 = async (
    request: SearchAvailabilitiesV3Request
  ): Promise<DateAvailabilitiesResponse> => {
    const { data } = await availabilitiesApi.getTimeBasedAvailabilitiesV3({
      SearchAvailabilitiesV3Request: request,
    })
    return data
  }

  const getProviderBasedAvailabilitiesV3 = async (
    request: SearchAllProvidersRequest
  ): Promise<DateAvailabilitiesWithPreferredResponse> => {
    const { data } = await availabilitiesApi.getProviderBasedAvailabilitiesV3({
      SearchAllProvidersRequest: request,
    })
    return data
  }

  const getOneProviderAvailabilitiesV3 = async (
    providerId: number,
    request: SearchAvailabilitiesV3Request
  ): Promise<DateAvailabilitiesResponse> => {
    const { data } = await availabilitiesApi.getOneProvidersAvailabilitiesV3({
      provider_id: providerId,
      SearchAvailabilitiesV3Request: request,
    })
    return data
  }

  const createCharge = async (
    userId: string,
    amount: number,
    description: string,
    meta?: Record<string, unknown>,
    capture?: boolean
  ): Promise<CreateChargeResponse> => {
    const params = { ...(capture && { capture }) }
    const endpoint = `/v1/members/${userId}/charges`

    const response = await api.post<CreateChargeResponse>(
      endpoint,
      {
        amount,
        description,
        meta,
      },
      { params }
    )

    if (!response.ok) {
      throw new Error(response.problem || 'Failed to create charge')
    }

    return response.data!
  }

  /* istanbul ignore next */
  const cancelAppointment = async (appointmentId: number): Promise<CancelAppointmentResponse> => {
    const response = await api.post<CancelAppointmentResponse, unknown>(
      `/v1/appointments/${appointmentId}/cancel`
    )
    if (response.ok && response.data) {
      return response.data
    }
    const errorMessage = 'Error cancelling appointment via Silkroad'
    logger.error(errorMessage, {
      ok: response?.ok,
      problem: response?.problem,
      data: response?.data,
      status: response?.status,
      duration: response?.duration,
    })
    throw new Error(errorMessage)
  }

  /* istanbul ignore next */
  const rescheduleAppointment = async (appointmentId: number): Promise<void> => {
    const response = await api.post<undefined, unknown>(
      `/v1/appointments/${appointmentId}/reschedule`
    )
    if (response.ok) {
      return
    }
    const errorMessage = 'Error rescheduling appointment via Silkroad'
    logger.error(errorMessage, {
      ok: response?.ok,
      problem: response?.problem,
      data: response?.data,
      status: response?.status,
      duration: response?.duration,
    })
    throw new Error(errorMessage)
  }

  /* istanbul ignore next */
  const startFlow = async ({
    episodeId,
    flowType,
  }: {
    episodeId: string
    flowType: FlowType
  }): Promise<void> => {
    const body: StartFlowPayload = { episode_id: episodeId, type: flowType }
    const response = await api.post<undefined, unknown>(`/v1/flows`, body)
    if (response.ok) {
      return
    }
    const errorMessage = 'Error starting flow via Silkroad'
    logger.error(errorMessage, {
      ok: response?.ok,
      problem: response?.problem,
      data: response?.data,
      status: response?.status,
      duration: response?.duration,
    })
    throw new Error(errorMessage)
  }

  return {
    // Combine legacy dialogue/services support with api sauce client exports
    ...dialogueServicesClient,
    cancelAppointment,
    createCharge,
    getTimeBasedAvailabilitiesV3,
    getProviderBasedAvailabilitiesV3,
    getOneProviderAvailabilitiesV3,
    rescheduleAppointment,
    startFlow,
  }
}

export default { create }
export type SilkroadClient = ReturnType<typeof create>
